/**
 * Fortune Telling (おみくじ) Feature
 * Handles the fortune-telling functionality for logged-in Shopify customers
 * crash-gate用
 */

function normalizeRate(value) {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return 0;
  // 0〜100にクランプ
  const clamped = Math.min(100, Math.max(0, n));
  // 小数第2位に丸め
  return Math.round(clamped * 100) / 100;
}

class FortuneTelling {
  constructor(container) {
    this.container = container;
    this.startButton = container.querySelector('[data-fortune-telling-start]');
    this.startImage = container.querySelector('.fortune-telling__start-image');
    this.animationContainer = container.querySelector('.fortune-telling__animation-container');
    this.resultContainer = container.querySelector('.fortune-telling__result-container');
    this.resultImage = container.querySelector('.fortune-telling__result-image');
    
    // Get configuration from JSON script tag
    const configScript = container.querySelector('[data-fortune-telling-config]');
    const resultsScript = container.querySelector('[data-fortune-telling-results]');
    
    if (configScript) {
      this.config = JSON.parse(configScript.textContent);
    } else {
      // Default configuration
      this.config = {
        winRates: {
          daikichi: 5,
          chukichi: 10,
          shokichi: 15,
          suekichi: 20,
          kyo: 50
        },
        animationDuration: 3000,
        animationImageSwitchInterval: 200
      };
    }

    // Normalize winRates (text settings may come as strings)
    const normalized = {};
    const baseRates = this.config.winRates || {};
    for (const [key, val] of Object.entries(baseRates)) {
      normalized[key] = normalizeRate(val);
    }
    this.config.winRates = normalized;
    
    if (resultsScript) {
      try {
        const jsonText = resultsScript.textContent.trim();
        this.resultImages = JSON.parse(jsonText);
      } catch (error) {
        this.resultImages = {
          daikichi: null,
          chukichi: null,
          shokichi: null,
          suekichi: null,
          kyo: null
        };
      }
    } else {
      this.resultImages = {
        daikichi: null,
        chukichi: null,
        shokichi: null,
        suekichi: null,
        kyo: null
      };
    }
    
    this.isAnimating = false;
    this.animationInterval = null;
    this.currentAnimationImage = 1;
    
    this.init();
  }
  
  init() {
    if (!this.startButton) return;
    
    this.startButton.addEventListener('click', () => {
      this.startFortuneTelling();
    });
  }
  
  /**
   * Start the fortune-telling process
   */
  async startFortuneTelling() {
    if (this.isAnimating) return;
    
    const customerLoggedIn = this.container.dataset.customerLoggedIn === 'true';
    const alreadyDrawn = this.container.dataset.alreadyDrawn === 'true';
    
    if (!customerLoggedIn || alreadyDrawn) {
      return;
    }
    
    this.isAnimating = true;
    this.startButton.disabled = true;
    this.startButton.classList.add('fortune-telling__button--disabled');
    
    // Hide start image
    if (this.startImage) {
      this.startImage.style.display = 'none';
    }
    
    // Show and start animation
    this.animationContainer.style.display = 'block';
    this.startAnimation();
    
    // Wait for animation duration
    await this.sleep(this.config.animationDuration);
    
    // Stop animation
    this.stopAnimation();
    this.animationContainer.style.display = 'none';
    
    // Draw result
    const result = this.drawResult();
    
    // Show result
    this.showResult(result);
    
    // Update customer metafield (result を渡す)
    const updated = await this.updateCustomerMetafield(result);

    if (updated) {
      this.container.dataset.alreadyDrawn = 'true';
      this.startButton.disabled = true;
      this.startButton.classList.remove('fortune-telling__button--active');
      this.startButton.classList.add('fortune-telling__button--disabled');
      this.startButton.textContent = this.getLocalizedText('already_drawn');
    } else {
      // 失敗したら再挑戦できるように戻す
      console.warn('[fortune] metafield update failed');
      this.startButton.disabled = false;
      this.startButton.classList.remove('fortune-telling__button--disabled');
    }
    
    // Update button state
    this.container.dataset.alreadyDrawn = 'true';
    this.startButton.disabled = true;
    this.startButton.classList.remove('fortune-telling__button--active');
    this.startButton.classList.add('fortune-telling__button--disabled');
    
    // Update button text to "already drawn" message
    this.startButton.textContent = this.getLocalizedText('already_drawn');
    
    this.isAnimating = false;
  }
  
  /**
   * Start the animation by switching images
   */
  startAnimation() {
    const image1 = this.animationContainer.querySelector('.fortune-telling__animation-image--1');
    const image2 = this.animationContainer.querySelector('.fortune-telling__animation-image--2');
    
    if (!image1) return;

    if (!image2) {
      image1.style.display = 'block';
      return;
    }
    
    image1.style.display = 'block';
    image2.style.display = 'none';
    this.currentAnimationImage = 1;
    
    this.animationInterval = setInterval(() => {
      if (this.currentAnimationImage === 1) {
        image1.style.display = 'none';
        image2.style.display = 'block';
        this.currentAnimationImage = 2;
      } else {
        image1.style.display = 'block';
        image2.style.display = 'none';
        this.currentAnimationImage = 1;
      }
    }, this.config.animationImageSwitchInterval);
  }
  
  /**
   * Stop the animation
   */
  stopAnimation() {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
  }
  
  /**
   * Draw a random result based on win rates
   */
  drawResult() {
    const results = ['daikichi', 'chukichi', 'shokichi', 'suekichi', 'kyo'];

    const total = results.reduce((sum, key) => {
      const v = Number(this.config.winRates?.[key]) || 0;
      return sum + v;
    }, 0);

    if (total <= 0) {
      return 'kyo';
    }

    const random = Math.random() * total;
    let cumulative = 0;

    for (const key of results) {
      cumulative += Number(this.config.winRates?.[key]) || 0;
      if (random <= cumulative) return key;
    }

    return 'kyo';
  }
  
  /**
   * Show the result image
   */
  showResult(result) {
    const resultImageUrl = this.resultImages[result];
    
    let isValidUrl = false;
    
    if (resultImageUrl) {
      if (typeof resultImageUrl === 'string' && resultImageUrl.trim() !== '' && resultImageUrl !== 'null') {
        isValidUrl = true;
      }
      if (typeof resultImageUrl === 'string' && resultImageUrl.startsWith('http')) {
        isValidUrl = true;
      }
    }
    
    if (!isValidUrl) {
      if (this.resultContainer) {
        this.resultContainer.replaceChildren();

        const wrapper = document.createElement('div');
        wrapper.style.padding = '2rem';
        wrapper.style.textAlign = 'center';

        const p1 = document.createElement('p');
        p1.textContent = '結果画像が設定されていません。テーマエディタで結果画像を設定してください。';

        const p2 = document.createElement('p');
        p2.textContent = `結果: ${result}`;

        wrapper.appendChild(p1);
        wrapper.appendChild(p2);

        this.resultContainer.appendChild(wrapper);
        this.resultContainer.style.display = 'block';
      }
      return;
    }
    
    if (!this.resultImage) {
      this.resultImage = this.container.querySelector('.fortune-telling__result-image');
    }
    
    if (!this.resultImage) {
      return;
    }
    
    this.resultImage.src = resultImageUrl;
    this.resultImage.alt = `おみくじ結果: ${result}`;
    
    if (this.resultContainer) {
      this.resultContainer.style.display = 'block';
    }
    
    const img = new Image();
    img.onload = () => {
      if (this.resultContainer) {
        this.resultContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    img.onerror = () => {
      if (this.resultContainer) {
        this.resultContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    img.src = resultImageUrl;
    
    this.resultImage.src = resultImageUrl;
  }
  
  /**
   * Update customer metafield to mark as drawn and save result
   */
  async updateCustomerMetafield(result) {
    const customerId = this.getCustomerId();
    if (!customerId) {
      console.warn('[fortune] customerId not found');
      return false;
    }

    // ★ AWS Lambda エンドポイント（masdpmbm55 を実際のAPI Gateway IDに差し替えてください）
    const customEndpoint =
      'https://masdpmbm55.execute-api.ap-northeast-1.amazonaws.com/fortune-telling/update-metafield';

    try {
      const response = await fetch(customEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId, result }),
      });

      const text = await response.text();
      console.log('[fortune] API status:', response.status, 'body:', text);

      return response.ok;
    } catch (error) {
      console.error('[fortune] API fetch failed:', error);
      return false;
    }
  }
  
  /**
   * Get customer ID from page data or global variables
   */
  getCustomerId() {
    const customerId = this.container.dataset.customerId;
    if (customerId) return customerId;
    
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.customerId) {
      return window.Shopify.customerId;
    }
    
    return null;
  }
  
  /**
   * Get localized text from Shopify translation system
   */
  getLocalizedText(key) {
    if (typeof window !== 'undefined' && window.theme && window.theme.strings) {
      const translationKey = `fortune_telling.${key}`;
      if (window.theme.strings[translationKey]) {
        return window.theme.strings[translationKey];
      }
    }
    
    const translations = {
      'already_drawn': '既に実施済みです',
      'login_required': 'ログインしてください',
      'start_button': 'おみくじスタート'
    };
    
    return translations[key] || key;
  }
  
  /**
   * Sleep utility
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Initialize fortune-telling on page load
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[data-section-type="fortune-telling"]');
  containers.forEach(container => {
    new FortuneTelling(container);
  });
});

// Re-initialize when Shopify theme editor loads sections
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('[data-section-type="fortune-telling"]');
    if (container) {
      new FortuneTelling(container);
    }
  });
}